import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";
import PaymentsIcon from "@mui/icons-material/Payments";
import ShieldIcon from "@mui/icons-material/Shield";
import VerifiedIcon from "@mui/icons-material/VerifiedUser";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import {
  listMyAppointmentsPatient,
  normalizeAppointmentList,
  PATIENT_APPTS_QUERY_KEY,
} from "@/api/appointmentApi";
import { formatSlotStart } from "@/lib/appointmentTimeFormat";
import {
  createOrder,
  MY_PAYMENTS_QUERY_KEY,
  verifyPayment,
} from "@/api/paymentApi";
import { PageHeader } from "@/components/common/PageHeader";
import { useAuth } from "@/auth/AuthContext";
import {
  openRazorpayCheckout,
  type RazorpayHandlerResponse,
} from "@/lib/razorpay";

type PayState =
  | { phase: "idle" }
  | { phase: "creatingOrder" }
  | { phase: "awaitingCheckout" }
  | { phase: "verifying" }
  | { phase: "success" }
  | { phase: "error"; message: string }
  | { phase: "cancelled" };

export default function PaymentPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { fullName, email } = useAuth();
  const [state, setState] = useState<PayState>({ phase: "idle" });

  const { data: rawAppts, isLoading } = useQuery({
    queryKey: PATIENT_APPTS_QUERY_KEY,
    queryFn: listMyAppointmentsPatient,
  });
  const appts = useMemo(
    () => normalizeAppointmentList(rawAppts),
    [rawAppts],
  );

  const appointment = useMemo(
    () => appts.find((a) => a.id === id),
    [appts, id],
  );

  const handlePay = useCallback(async () => {
    if (!appointment) return;

    setState({ phase: "creatingOrder" });
    try {
      const order = await createOrder(appointment.id);

      if (order.paymentWaived) {
        qc.invalidateQueries({ queryKey: PATIENT_APPTS_QUERY_KEY });
        qc.invalidateQueries({ queryKey: MY_PAYMENTS_QUERY_KEY });
        setState({ phase: "success" });
        navigate("/patient/appointments?paid=1", { replace: true });
        return;
      }

      if (!order.razorpayOrderId || !order.razorpayKeyId) {
        setState({
          phase: "error",
          message:
            "Payment is not available right now. Please try again in a moment.",
        });
        return;
      }

      setState({ phase: "awaitingCheckout" });

      await openRazorpayCheckout({
        key: order.razorpayKeyId,
        amount: order.amountPaise,
        currency: order.currency || "INR",
        order_id: order.razorpayOrderId,
        name: "ChikitsaLink",
        description: appointment.doctorName
          ? `Consultation with ${appointment.doctorName}`
          : "Doctor consultation",
        image: "/logo-mark.svg",
        prefill: {
          name: fullName,
          email: email,
        },
        notes: {
          appointmentId: appointment.id,
        },
        theme: { color: "#2E7D32" },
        handler: async (resp: RazorpayHandlerResponse) => {
          setState({ phase: "verifying" });
          try {
            await verifyPayment({
              razorpayOrderId: resp.razorpay_order_id,
              razorpayPaymentId: resp.razorpay_payment_id,
              razorpaySignature: resp.razorpay_signature,
            });
            qc.invalidateQueries({ queryKey: PATIENT_APPTS_QUERY_KEY });
            qc.invalidateQueries({ queryKey: MY_PAYMENTS_QUERY_KEY });
            setState({ phase: "success" });
            navigate("/patient/appointments?paid=1", { replace: true });
          } catch (err) {
            setState({
              phase: "error",
              message:
                "We received your payment but couldn't confirm it. Please contact support with your payment reference (from your receipt or bank SMS, if available).",
            });
          }
        },
        modal: {
          ondismiss: () => {
            setState((s) =>
              s.phase === "verifying" || s.phase === "success"
                ? s
                : { phase: "cancelled" },
            );
          },
        },
      });
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : "Could not start the payment. Please try again.";
      setState({ phase: "error", message });
    }
  }, [appointment, email, fullName, navigate, qc]);

  if (isLoading) {
    return (
      <Box sx={{ display: "grid", placeItems: "center", minHeight: "50vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!appointment) {
    return (
      <Box>
        <PageHeader title="Payment" />
        <Alert severity="error">
          We couldn't find that appointment. It may have been cancelled or
          it doesn't belong to your account.
        </Alert>
        <Box mt={2}>
          <Button
            variant="contained"
            onClick={() => navigate("/patient/appointments")}
          >
            Back to my appointments
          </Button>
        </Box>
      </Box>
    );
  }

  const alreadyConfirmed = appointment.status === "CONFIRMED";
  const cancelled = appointment.status === "CANCELLED";
  const fee = appointment.consultationFee;

  const busy =
    state.phase === "creatingOrder" ||
    state.phase === "awaitingCheckout" ||
    state.phase === "verifying";

  return (
    <Box sx={{ maxWidth: 720, mx: "auto" }}>
      <PageHeader
        title="Confirm & pay"
        subtitle="Your slot is held as PENDING until payment is completed."
      />

      {alreadyConfirmed && (
        <Alert severity="success" sx={{ mb: 3 }}>
          This appointment is already confirmed. No payment needed.
          <Button
            size="small"
            sx={{ ml: 2 }}
            onClick={() => navigate("/patient/appointments")}
          >
            Go to my appointments
          </Button>
        </Alert>
      )}

      {cancelled && (
        <Alert severity="error" sx={{ mb: 3 }}>
          This appointment has been cancelled and cannot be paid for.
        </Alert>
      )}

      {state.phase === "error" && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {state.message}
        </Alert>
      )}

      {state.phase === "cancelled" && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          Payment was cancelled. You can try again whenever you're ready.
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: { xs: 2.5, md: 3.5 } }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Avatar sx={{ bgcolor: "primary.main", width: 56, height: 56 }}>
            {appointment.doctorName?.[0] ?? "D"}
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography fontWeight={700}>
              {appointment.doctorName ?? "Doctor"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {appointment.doctorSpecialization ?? "Consultation"}
            </Typography>
          </Box>
        </Stack>

        <Divider sx={{ my: 2.5 }} />

        <SummaryRow
          label="Date & time"
          value={formatSlotStart(appointment.slotStart, appointment.timeZone)}
        />
        <SummaryRow label="Appointment id" value={appointment.id} mono />
        {appointment.patientNote && (
          <SummaryRow label="Your note" value={`"${appointment.patientNote}"`} />
        )}

        <Divider sx={{ my: 2.5 }} />

        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="subtitle1" color="text.secondary">
            Consultation fee
          </Typography>
          <Typography variant="h4" fontWeight={800} color="primary.dark">
            {fee != null ? `₹${fee.toFixed(2)}` : "—"}
          </Typography>
        </Stack>

        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          sx={{ mt: 1, color: "text.secondary" }}
        >
          <LockIcon fontSize="small" />
          <Typography variant="caption">
            Standard secure checkout — UPI, cards, netbanking and wallets supported.
          </Typography>
        </Stack>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          justifyContent="flex-end"
          sx={{ mt: 3 }}
        >
          <Button
            variant="text"
            onClick={() => navigate("/patient/appointments")}
            disabled={busy}
          >
            Pay later
          </Button>
          <Button
            variant="contained"
            size="large"
            disabled={busy || alreadyConfirmed || cancelled || fee == null}
            startIcon={busy ? <CircularProgress size={18} color="inherit" /> : <PaymentsIcon />}
            onClick={handlePay}
          >
            {busy
              ? state.phase === "verifying"
                ? "Confirming…"
                : state.phase === "awaitingCheckout"
                  ? "Opening checkout…"
                  : "Starting payment…"
              : fee != null
                ? `Pay ₹${fee.toFixed(2)} securely`
                : "Pay now"}
          </Button>
        </Stack>
      </Paper>

      <TrustBar />
    </Box>
  );
}

function SummaryRow({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      spacing={2}
      sx={{ py: 0.75 }}
    >
      <Typography color="text.secondary">{label}</Typography>
      <Typography
        sx={{
          fontFamily: mono ? "ui-monospace, SFMono-Regular, monospace" : "inherit",
          fontSize: mono ? 13 : undefined,
          textAlign: "right",
          wordBreak: "break-all",
        }}
        fontWeight={mono ? 500 : 600}
      >
        {value}
      </Typography>
    </Stack>
  );
}

function TrustBar() {
  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={2}
      sx={{ mt: 3, color: "text.secondary" }}
    >
      <TrustItem
        icon={<ShieldIcon fontSize="small" />}
        text="Industry-standard secure payment checkout"
      />
      <TrustItem
        icon={<VerifiedIcon fontSize="small" />}
        text="Server-side signature verification"
      />
      <TrustItem
        icon={<LockIcon fontSize="small" />}
        text="Secure sign-in · Access-controlled APIs"
      />
    </Stack>
  );
}

function TrustItem({
  icon,
  text,
}: {
  icon: React.ReactNode;
  text: string;
}) {
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      {icon}
      <Typography variant="caption">{text}</Typography>
    </Stack>
  );
}
